<template>
  <section v-if="!isLoading">
    <div class="relative pb-7">
      <h1
        class="absolute top-7 left-0 right-0 bottom-0 text-orange-600 text-lg font-semibold text-center"
      ></h1>
    </div>

    <div
      class="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 cursor-pointer shadow-md transition"
    >
      <BackSvg @click="$router.go(-1)" />
    </div>
    <div class="my-2">
      <div class="flex flex-col items-center justify-center">
        <h1 class="text-center text-orange-600 text-lg font-semibold">
          Approval Number
        </h1>
        <h2 class="text-base text-zinc-500">{{ itemDetail.approvalno }}</h2>
        <span
          class="text-xs px-3 py-2 rounded-full font-medium"
          :class="{
            'bg-green-200 text-green-700': itemDetail.status === 'processed',
            'bg-gray-200 text-gray-700': itemDetail.status === 'pending',
            'bg-red-200 text-red-700':
              itemDetail.status === 'voided' || itemDetail.status === 'cancel',
          }"
        >
          {{
            itemDetail.status === "processed"
              ? "Completed"
              : itemDetail.status === "voided"
              ? "Void"
              : itemDetail.status === "cancel"
              ? "Cancelled"
              : "Pending"
          }}
        </span>
      </div>
    </div>
    <div
      class="relative bg-white border border-gray-200 rounded-2xl shadow-lg my-4 overflow-hidden"
    >
      <!-- Left cutout -->
      <div
        class="absolute top-1/2 -left-3 -translate-y-1/2 w-6 h-6 bg-gray-50 border border-gray-200 rounded-full"
      ></div>

      <!-- Right cutout -->
      <div
        class="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 bg-gray-50 border border-gray-200 rounded-full"
      ></div>

      <!-- Top Section -->
      <div
        class="flex flex-col gap-1 bg-gradient-to-r from-orange-50 to-white p-5"
      >
        <span class="text-base font-semibold text-orange-600">
          {{ itemDetail.provider_name }}
        </span>
        <span> {{ itemDetail.provider_pf_category }} </span>
      </div>
      <!-- Divider -->
      <div class="border-t border-dashed border-gray-300"></div>

      <!-- Bottom Section -->
      <div
        class="flex gap-2 justify-center flex-col items-center py-4 bg-gray-50"
      >
        <span class="text-xs text-zinc-400">
          <h5>{{ dateFormatMonthDateYear(itemDetail.date_generate) }}</h5>
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <button
        @click="exportPDF(itemDetail)"
        type="submit"
        class="mx-auto w-full py-5 rounded-xl bg-[#E87055] text-white font-semibold transition duration-200 flex items-center justify-center"
      >
        Download Letter of Approval
      </button>

      <button
        v-if="itemDetail.status === 'pending'"
        @click="cancelHandler()"
        type="submit"
        class="mx-auto w-full py-5 rounded-xl bg-secondaryPalette text-white font-semibold transition duration-200 flex items-center justify-center"
      >
        Cancel Transaction
      </button>
    </div>
  </section>
  <div v-else>
    <div class="flex items-center justify-center h-48" v-if="isLoading">
      <svg
        class="w-10 h-10 animate-spin text-orange-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        ></path>
      </svg>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, inject } from "vue";
import BackSvg from "@/components/svgs/BackSvg.vue";
import { useRoute } from "vue-router";
import pdfGenerateHandler from "@/utils/reusables/pdfLetter";
import reusableBase from "@/utils/composables/format";
export default {
  components: {
    BackSvg,
  },
  setup() {
    const { exportPDF } = pdfGenerateHandler();
    const { dateFormatMonthDateYear } = reusableBase();
    const route = useRoute();
    const Swal = inject("$swal");
    const isLoading = ref(true);
    const axios = inject("$axios");
    const itemDetail = ref([]);
    onMounted(async () => {
      try {
        isLoading.value = true;
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const { data } = await axios.get(
          `/meta/api/amap/resources/transaction/loa/${route.params.id}`
        );
        itemDetail.value = data.result;
        await delay(2000);
        isLoading.value = false;
      } catch (error) {
        console.log("render", error);
      }
    });
    const cancelHandler = () => {
      Swal.fire({
        title: "Confirm Transaction Cancellation",
        text: "Are you sure you want to cancel this transaction?",
        showCancelButton: true,
        confirmButtonColor: "#E87055",
        cancelButtonColor: "#6B7280",
        confirmButtonText: "Yes, Cancel It",
        cancelButtonText: "No, Keep It",
        width: 500,

        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),

        preConfirm: async () => {
          try {
            await cancelledTransaction();
          } catch (error) {
            Swal.showValidationMessage(
              `Cancellation failed. Please try again.`
            );
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            icon: "success",
            title: "Transaction Cancelled",
            text: "The transaction has been successfully cancelled.",
            confirmButtonText: "OK",
            confirmButtonColor: "#16a34a",
          }).then(() => {
            itemDetail.value.status = "cancel";
          });
        }
      });
    };
    const cancelledTransaction = async () => {
      try {
        await axios.put(
          `/meta/api/amap/resources/member/transaction/${itemDetail.value.approvalno}`
        );
      } catch (error) {
        console.log("render", error);
      }
    };
    return {
      isLoading,
      itemDetail,
      exportPDF,
      cancelHandler,
      dateFormatMonthDateYear,
      cancelledTransaction,
    };
  },
};
</script>
